import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { FaSearch, FaArrowLeft, FaLightbulb, FaCheckCircle, FaChartLine } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { QuizFeedbackBubble, useRandomCharacter } from '../components/CharacterBubble';

// Conseils SEO du coach
const seoTips = [
  "Un bon titre = 60 caractères max avec le mot-clé principal !",
  "La meta description doit donner envie de cliquer (160 car. max).",
  "Structure ton article avec des H2 et H3 pertinents.",
  "Les liens internes renforcent l'architecture de ton site.",
  "Un contenu de qualité > le bourrage de mots-clés !",
  "Pense mobile-first : Google indexe d'abord la version mobile.",
  "Les images doivent avoir un attribut alt descriptif."
];

function SEOPage() {
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [currentExercise, setCurrentExercise] = useState(0);
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showTip, setShowTip] = useState(true);
  const [totalScore, setTotalScore] = useState(0);
  const navigate = useNavigate();
  const { character: coach } = useRandomCharacter('seo');
  const [currentTip] = useState(seoTips[Math.floor(Math.random() * seoTips.length)]);

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      const response = await api.get('/seo/lessons');
      setLessons(response.data);
    } catch (error) {
      console.error('Error fetching lessons:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectLesson = (lesson) => {
    setSelectedLesson(lesson);
    setCurrentExercise(0);
    setAnswer('');
    setFeedback(null);
  };

  const submitAnswer = async (e) => {
    e.preventDefault();
    if (!answer.trim() || submitting) return;

    setSubmitting(true);

    try {
      const exercise = selectedLesson.exercises[currentExercise];
      const response = await api.post('/seo/exercise', {
        lessonId: selectedLesson.id,
        exerciseId: exercise.id,
        answer: answer
      });

      setFeedback(response.data);
      if (response.data.score) {
        setTotalScore(totalScore + response.data.score);
      }

      setTimeout(() => {
        if (currentExercise < selectedLesson.exercises.length - 1) {
          setCurrentExercise(currentExercise + 1);
          setAnswer('');
          setFeedback(null);
        }
      }, 4000);
    } catch (error) {
      console.error('Error submitting answer:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const resetLesson = () => {
    setSelectedLesson(null);
    setCurrentExercise(0);
    setAnswer('');
    setFeedback(null);
    setTotalScore(0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F4E3' }}>
        <div className="onomatopoeia">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
      <header className="border-b-4 border-black" style={{ background: '#FFB703' }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => { resetLesson(); navigate('/dashboard'); }}
            className="flex items-center space-x-2 comic-button bg-white text-black hover:bg-gray-100"
            data-testid="back-button"
          >
            <FaArrowLeft className="inline" /> 
            <span className="hidden sm:inline">{selectedLesson ? 'Changer Leçon' : 'Retour Dashboard'}</span>
            <span className="sm:hidden">Retour</span>
          </button>
          <h1 className="text-2xl md:text-3xl comic-title" style={{ color: '#1B4965' }}>
            <FaSearch className="inline mr-2" />
            Cours SEO
          </h1>
          {totalScore > 0 && (
            <div className="bg-white px-4 py-2 rounded-lg border-2 border-black">
              <FaChartLine className="inline mr-1 text-green-500" />
              <span className="font-bold">{totalScore} pts</span>
            </div>
          )}
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Tip du coach SEO */}
        <AnimatePresence>
          {showTip && coach && !selectedLesson && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-4xl mx-auto mb-6"
            >
              <div className="speech-bubble p-4">
                <div className="flex items-start space-x-3">
                  <img
                    src={coach.image}
                    alt={coach.name}
                    className="w-14 h-14 object-contain rounded-full border-2 border-black bg-white"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <p className="font-bold comic-text" style={{ color: '#1B4965' }}>{coach.name} :</p>
                      <button 
                        onClick={() => setShowTip(false)}
                        className="text-gray-400 hover:text-gray-600 text-sm"
                      >✕</button>
                    </div>
                    <p className="text-gray-700">
                      <FaLightbulb className="inline text-yellow-500 mr-1" />
                      {currentTip}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {!selectedLesson ? (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="comic-title text-3xl mb-6 text-center" style={{ color: '#1B4965' }}>
              Choisis une leçon SEO
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {lessons.map((lesson, idx) => (
                <motion.div
                  key={lesson.id}
                  whileHover={{ scale: 1.05 }}
                  className="comic-panel cursor-pointer hover:bg-yellow-50"
                  onClick={() => selectLesson(lesson)}
                  data-testid={`lesson-${idx}`}
                >
                  <div className="text-4xl mb-3">📚</div>
                  <h3 className="comic-text text-xl mb-2">{lesson.topic}</h3>
                  <p className="text-sm text-gray-600 mb-3">{lesson.content.substring(0, 100)}...</p>
                  <div className="comic-badge bg-yellow-500 text-black text-xs">
                    {lesson.exercises.length} exercices
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ) : (
          <div className="max-w-4xl mx-auto">
            {/* Lesson Info */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="comic-panel mb-6"
            >
              <h2 className="comic-title text-3xl mb-4" style={{ color: '#1B4965' }}>
                {selectedLesson.topic}
              </h2>
              <div className="speech-bubble bg-yellow-50">
                <p className="text-base">{selectedLesson.content}</p>
              </div>
            </motion.div>

            {/* Exercise */}
            {currentExercise < selectedLesson.exercises.length ? (
              <motion.div
                key={currentExercise}
                initial={{ x: 300, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                className="comic-panel"
              >
                <div className="mb-4">
                  <span className="comic-badge bg-purple-500 text-white">
                    Exercice {currentExercise + 1}/{selectedLesson.exercises.length}
                  </span>
                </div>

                <h3 className="comic-text text-2xl mb-4">
                  {selectedLesson.exercises[currentExercise].title}
                </h3>
                <p className="text-base mb-6">
                  {selectedLesson.exercises[currentExercise].instruction}
                </p>

                <form onSubmit={submitAnswer} data-testid="exercise-form">
                  <textarea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Ta réponse ici..."
                    className="w-full input-comic h-32 resize-none"
                    disabled={submitting}
                    data-testid="answer-textarea"
                  />
                  <button
                    type="submit"
                    disabled={submitting || !answer.trim()}
                    className="w-full comic-button comic-button-navy text-xl mt-4"
                    data-testid="submit-answer-button"
                  >
                    {submitting ? 'ÉVALUATION...' : 'SOUMETTRE'}
                  </button>
                </form>

                {feedback && (
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    className="mt-6"
                  >
                    <QuizFeedbackBubble 
                      isCorrect={feedback.score >= 7}
                      feedback={`${feedback.feedback} Score: ${feedback.score}/10 ${'⭐'.repeat(feedback.score)}`}
                      context="seo"
                    />
                  </motion.div>
                )}
              </motion.div>
            ) : (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="comic-panel text-center"
              >
                <div className="onomatopoeia mb-6">TERMINÉ!</div>
                <div className="text-6xl mb-4">🏆</div>
                <h2 className="comic-title text-3xl mb-4" style={{ color: '#1B4965' }}>
                  Leçon Terminée!
                </h2>
                <div className="space-x-4">
                  <button
                    onClick={resetLesson}
                    className="comic-button comic-button-navy"
                    data-testid="choose-another-button"
                  >
                    Autre Leçon
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="comic-button bg-gray-200"
                  >
                    Dashboard
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SEOPage;
